import { GtmData } from './../types/gtm';
import { FinanceWidgetDuck } from './../redux/financeWidget/financeWidget.duck';
import { GlobalDuck } from './../redux/global/global.duck';
import { ContentService } from './../services/content/content.service';
import { UIDuck } from './../redux/commonDucks/ui.duck';
import { IVehicleTransformed } from './../services/stock/models/stockItems.service.model';
import { CarJourneyType } from './../services/carList/carList.types';
import { FilterDuck } from './../redux/filters/filter.duck';
import { Redux } from './../redux/redux.interface';
import { batch, useDispatch, useSelector } from 'react-redux';
import { ContentDuck } from './../redux/content/content.duck';
import { Pages } from './../types/pages';
import { useCreateMopAndGoToCheckout } from './useCreateMopAndGoToCheckout';
import useTranslations from './useTranslations';
import { useCarDetailsDuck } from './useCarDetailsDuck';
import { useGTM } from './useGTM';
import { useDealCreate } from './useCreateDeal';
import { useFeatureSwitch } from './useFeatureSwitch';
import { useCarStockJourneyCheck } from './useCarStockJourneyCheck';
import { useRouter } from 'next/router';
import { buildParameterizedPathname, extractParamsFromPathname } from '@utils/url.utils';
import { getSessionIdCookie, setLastUpdateSetting } from '@utils/Session.utils';
import routes from '../constants/routes';
import StockService from '../services/stock/stock.service';
import { gaCrossDomainParameter } from '@utils/gaCrossDomainParameter';
import { checkLastSaveSettings } from '@utils/page.utils';
import { FinanceStatuses } from '../types/financeStatuses';
import { useUserDuck } from '@hooks/useUserDuck';
import UserDuck from '../redux/user/user.duck';
import { EUserLoginType } from '../interfaces/User';
import { useDealerDuck } from '@hooks/useDealerDuck';
import { dealerApi, DealService } from '../services';
import carDetailsDuck from '../redux/carDetails/carDetails.duck';
import { ModalVersion, PxVersion } from '../constants/main';
import { useFeatureSwitchEnabled } from '@hooks/useFeatureSwitchEnabled';
import { FEATURES_LIST, FeatureSwitchContext } from '../context/featureSwitchApp';
import { useExperiment } from './useExperiment';
import { useGlobalDuck } from '@hooks/useGlobalDuck';
import { usePlausible, UserEvent } from '@hooks/usePlausible';
import useSessionChangeListener from '@hooks/useSessionChangeListener';
import { getFeatureSwitch } from '@utils/featureSwitches.utils';
import { useContext } from 'react';
import { PX_STATUSES_APPROVED } from '../partExchange/constants/config';
import ConfiguratorDuck from '../redux/configurator/configurator.duck';

/**
 * Provides onClick function to get to next page based on current page for use on all continue buttons in app
 */
export const useContinueToNextPage = (
    page: Pages,
    translationKey?: string,
    position?: string
): { onClick: () => Promise<void> | void } => {
    const dispatch = useDispatch();
    const { plausibleUserEvent } = usePlausible();
    const router = useRouter();
    const createMopAndGoToCheckout = useCreateMopAndGoToCheckout();
    const content = useSelector((state: Redux) => ContentDuck.getContent(state));
    const { t, i18n } = useTranslations();
    const { userAuth } = useUserDuck();
    const {
        pushUserAction,
        pushAddToCart,
        getBodyStyleLabel,
        pushShareConfigurationSuccess,
        pushProductClick,
        pushToDataLayer,
    } = useGTM();
    const { pushTrigger } = useExperiment();
    const { carDetails, defaultConfiguration } = useCarDetailsDuck();
    const context = useContext(FeatureSwitchContext);
    const { dealer } = useDealerDuck();
    const { isStockJourney } = useCarStockJourneyCheck();
    const { handleContinueButtonChange } = useSessionChangeListener();
    const paymentJourney = useSelector((state) => FilterDuck.getPaymentJourneyType(state));
    const isCheckoutDisabled = useSelector((state: Redux) =>
        ContentDuck.isCheckoutDisable(
            state,
            isStockJourney ? CarJourneyType.STOCK : CarJourneyType.CONFIGURABLE,
            paymentJourney
        )
    );
    const autobizPxState = useSelector((state: Redux) => ConfiguratorDuck.getOwnState(state).pxState);
    const { dealCreate } = useDealCreate();
    const {
        isDealerSelectionCheckoutRedirectAllowed,
        isStockSummaryEnabled,
        isMTOSummaryEnabled,
        isDealerChoiceEnabled,
    } = useFeatureSwitch();
    const { carJourney } = useGlobalDuck();

    const pxVersion = getFeatureSwitch(FEATURES_LIST.FEATURE_SWITCH_PX_VERSION, context);

    const isScrappageEnabled = useFeatureSwitchEnabled(FEATURES_LIST.FEATURE_SWITCH_SHOW_SCRAPPAGE_MODAL);

    const pushShareConfiguration = () =>
        pushShareConfigurationSuccess(
            'basket',
            t(translationKey || 'configurator.interestedBox1.link.finance'),
            'clickOnContinue'
        );
    const pushBasketActions = () => {
        pushAddToCart(carDetails);
        pushUserAction({
            action: 'AddToBasket',
            label: getBodyStyleLabel(carDetails),
        });
        pushShareConfiguration();
        pushTrigger('onclick', page, 'continue', null, position);
    };
    const handleStockCarAvailabilityError = (vehicle: IVehicleTransformed) => {
        const { model, bodyStyle, nameplateBodyStyleSlug } = vehicle;
        const modelUrl = buildParameterizedPathname(routes.SELECTOR, CarJourneyType.STOCK, {
            nameplateBodyStyleSlug: nameplateBodyStyleSlug,
            modelId: model.id,
            bodyStyleId: bodyStyle.id,
        });

        dispatch(
            UIDuck.openModal({
                data: {
                    modelName: model.title,
                    modelUrl: modelUrl,
                },
                modalType: UIDuck.MODAL_TYPES.STOCK_CAR_UNAVAILABLE,
                modalVersion: ModalVersion.v2,
                modalProperties: {
                    centerModeOnDesktop: true,
                },
            })
        );
    };
    const updateDealAndGoToTheNextPage = async (
        newRoute: string,
        doCreateDeal: boolean,
        newStatus?: FinanceStatuses,
        withOptionsUpdate?: boolean
    ) => {
        dispatch(UIDuck.setLoading(5));
        try {
            if (doCreateDeal) {
                const deal = await dealCreate(newStatus, withOptionsUpdate);
                dispatch(carDetailsDuck.setCurrentDeal(deal));
            }
            router.push(newRoute);
        } catch (err: any) {
            console.error('Error creating the deal and going to the next page: ', err);
        } finally {
            dispatch(UIDuck.setLoading(-5));
        }
    };

    switch (page) {
        case Pages.trim:
            // Default configuration has preceedence on trim page
            return {
                onClick: () => {
                    batch(() => {
                        // Reset finance data after before select vehicle
                        dispatch(FinanceWidgetDuck.setFinanceWidgetOutput(null));
                        dispatch(FinanceWidgetDuck.setFinancePricesList(null));
                        dispatch(FinanceWidgetDuck.setFinanceQuote(null));

                        pushProductClick('trim', defaultConfiguration || carDetails);
                        pushToDataLayer({
                            eventCategory: 'SolRetail::VehicleFinition',
                            eventAction: 'Select::VehicleFinition',
                            eventLabel: carDetails.specPack.title,
                        } as GtmData);
                    });
                    router.push(
                        buildParameterizedPathname(routes.CAR, carJourney, {
                            nameplateBodyStyleSlug: extractParamsFromPathname(router.asPath).nameplateBodyStyleSlug,
                            speckPackSlug: carDetails.specPackSlug,
                            engineGearboxFuelSlug: carDetails.engineGearboxFuelSlug,
                            exteriorColourSlug: (defaultConfiguration || carDetails).exteriorColourSlug,
                            interiorColourSlug: (defaultConfiguration || carDetails).interiorColourSlug,
                        })
                    );
                },
            };
        case Pages.summary:
            return {
                onClick: () => {
                    pushBasketActions();
                    updateDealAndGoToTheNextPage(
                        isDealerChoiceEnabled && !isStockJourney ? routes.DEALER : routes.BASKET,
                        false
                    );
                },
            };
        case Pages.dealer:
            return {
                onClick: async () => {
                    const { data } = await dealerApi.updateDealer(dealer.externalId, getSessionIdCookie());
                    const { deal, financeQuote } = data;
                    batch(() => {
                        dispatch(carDetailsDuck.setCurrentDeal(deal));
                        dispatch(carDetailsDuck.setCurrentCarDetails(deal.fullProductConfiguration));
                        if (financeQuote) {
                            dispatch(FinanceWidgetDuck.setFinanceQuote(financeQuote));
                        }
                    });
                    updateDealAndGoToTheNextPage(routes.BASKET, false);
                },
            };
        case Pages.car:
            return {
                onClick: async () => {
                    pushBasketActions();

                    const continueToBasketPage = () => {
                        const isSummaryEnabled =
                            (isStockSummaryEnabled && isStockJourney) || (isMTOSummaryEnabled && !isStockJourney);

                        if (isDealerChoiceEnabled && !isSummaryEnabled) {
                            return updateDealAndGoToTheNextPage(routes.DEALER, true, FinanceStatuses.dealer, true);
                        }

                        updateDealAndGoToTheNextPage(
                            isSummaryEnabled ? routes.SUMMARY : routes.BASKET,
                            true,
                            isSummaryEnabled ? FinanceStatuses.vehicleSummary : FinanceStatuses.basket,
                            true
                        );
                    };

                    const hasActiveDeal = await handleContinueButtonChange(continueToBasketPage, false);

                    if (hasActiveDeal) return;

                    continueToBasketPage();
                },
            };
        case Pages.basket:
        case Pages.basketV2:
            return {
                onClick: async () => {
                    pushUserAction({
                        action: 'NavigateTo::Checkout',
                        label: 'Top Page',
                        eventName: 'uaevent',
                    });
                    pushShareConfiguration();
                    pushTrigger('onclick', 'basket', 'continue');
                    (window as any).DY = (window as any).DY || {};
                    (window as any).DY.recommendationContext = { type: 'CART', data: [carDetails?.lcdv16] };
                    if (checkLastSaveSettings().isExpired) {
                        const data = await ContentService.getSettings();
                        if (data) {
                            dispatch(ContentDuck.setContent({ ...content, settings: data }, i18n.language, i18n));
                            setLastUpdateSetting();
                        }
                    }

                    if (
                        pxVersion === PxVersion.v2 &&
                        autobizPxState &&
                        !PX_STATUSES_APPROVED.includes(autobizPxState.statusId)
                    ) {
                        dispatch(
                            UIDuck.openModal({
                                data: { data: {} },
                                modalType: UIDuck.MODAL_TYPES.INCOMPLETE_PX,
                                modalVersion: ModalVersion.v2,
                                modalProperties: {
                                    centerModeOnDesktop: true,
                                },
                            })
                        );

                        return;
                    }

                    if (isCheckoutDisabled) {
                        plausibleUserEvent(UserEvent.CheckoutDisabledModalOpen);
                        dispatch(
                            UIDuck.openModal({
                                data: { data: {} },
                                callbacks: {},
                                modalType: UIDuck.MODAL_TYPES.DISABLE_CHECKOUT,
                                modalVersion: ModalVersion.v2,
                            })
                        );
                        return;
                    }

                    if (userAuth.loginType === EUserLoginType.GUEST) {
                        dispatch(UserDuck.actionSetUserLogged(false));
                        dispatch(UserDuck.setUserAddress(null));
                        dispatch(UserDuck.setUserDetails(null));
                    }

                    if (isStockJourney) {
                        const { available } = await StockService.checkVehicleAvailability(carDetails.orders[0].uuid);

                        if (!available) {
                            handleStockCarAvailabilityError(carDetails);
                            return;
                        }
                    }

                    if (
                        isScrappageEnabled &&
                        carDetails?.importInputs?.isWithScrappage &&
                        carDetails?.importInputs?.ecologicalBonusScrappage > 0
                    ) {
                        dispatch(
                            UIDuck.openModal({
                                data: { data: {} },
                                callbacks: {
                                    onClick: async (_, value) => {
                                        const { data } = await DealService.applyScrappage(value, getSessionIdCookie());

                                        batch(() => {
                                            dispatch(carDetailsDuck.setCurrentDeal(data.deal));
                                            dispatch(
                                                carDetailsDuck.setCurrentCarDetails(data.deal.fullProductConfiguration)
                                            );
                                            if (data.financeQuote?.token) {
                                                dispatch(FinanceWidgetDuck.setFinanceQuote(data.financeQuote));
                                            }
                                            dispatch(GlobalDuck.setIsWithScrappageDone(true));
                                        });

                                        isDealerSelectionCheckoutRedirectAllowed
                                            ? router.push(gaCrossDomainParameter(routes.CHECKOUT_DELIVERY))
                                            : createMopAndGoToCheckout();
                                    },
                                },
                                modalType: UIDuck.MODAL_TYPES.SCRAPPAGE,
                                modalVersion: ModalVersion.v2,
                            })
                        );
                    } else {
                        isDealerSelectionCheckoutRedirectAllowed
                            ? router.push(gaCrossDomainParameter(routes.CHECKOUT_DELIVERY))
                            : createMopAndGoToCheckout();
                    }
                },
            };
        default:
            return {
                onClick: () => null,
            };
    }
};
